import React, { useEffect, useRef, useState } from "react";
import MaskedLines from "../common/MaskedLines";
import { heroContent, studioIdentity } from "../../data/site";
import { useAllowsHeavyMedia, usePrefersReducedMotion } from "../../hooks";
import "./Hero.css";

/**
 * Opening section: a full-viewport film with the studio statement set over it.
 *
 * Notes on the implementation:
 *  - The headline arrives through `MaskedLines`, triggered on mount rather
 *    than on scroll (the hero is always above the fold).
 *  - The poster is the film's own first frame, so the still and the first
 *    video frame are the same picture and there is no visible jump when
 *    playback begins.
 *  - If the visitor prefers reduced motion the film is not played at all; the
 *    poster becomes the background. An animated background is exactly the kind
 *    of thing that setting exists to suppress.
 *  - `useAllowsHeavyMedia` also keeps the film off phones and metered or slow
 *    connections, where the still frame is shown instead.
 *
 * @returns {JSX.Element}
 */
function Hero() {
  const videoElementRef = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const shouldLoadFilm = useAllowsHeavyMedia();
  const [hasVideoLoaded, setHasVideoLoaded] = useState(false);
  const [hasEntered, setHasEntered] = useState(false);

  // Trigger the entrance on the frame after mount, so the browser has painted
  // the starting state and will animate the transition rather than skip it.
  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => setHasEntered(true));
    return () => window.cancelAnimationFrame(frameId);
  }, []);

  // Pause the film when motion is suppressed, and keep it in sync if the
  // visitor changes the setting while the page is open.
  useEffect(() => {
    const videoElement = videoElementRef.current;
    if (!videoElement) return;

    if (prefersReducedMotion) {
      videoElement.pause();
    } else if (shouldLoadFilm) {
      // play() rejects when autoplay is blocked; the poster remains visible,
      // which is an acceptable outcome, so the rejection is swallowed.
      const playbackAttempt = videoElement.play();
      if (playbackAttempt && typeof playbackAttempt.catch === "function") {
        playbackAttempt.catch(() => {});
      }
    }
  }, [prefersReducedMotion, shouldLoadFilm]);

  const handleScrollToNextSection = () => {
    const nextSection = document.getElementById("usluge");
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
    }
  };

  return (
    <section
      className="hero"
      data-entered={hasEntered ? "true" : "false"}
      aria-label="Uvod"
    >
      <div className="hero__media">
        <img
          className="hero__poster"
          src={heroContent.posterUrl}
          alt=""
          aria-hidden="true"
          fetchpriority="high"
          data-hidden={hasVideoLoaded && shouldLoadFilm ? "true" : "false"}
        />
        {shouldLoadFilm && (
          <video
            ref={videoElementRef}
            className="hero__video"
            poster={heroContent.posterUrl}
            autoPlay
            loop
            muted
            playsInline
            /* "metadata", not "auto": the poster carries the hero until enough
               of the film has arrived to start playing. */
            preload="metadata"
            disablePictureInPicture
            tabIndex={-1}
            aria-hidden="true"
            onCanPlay={() => setHasVideoLoaded(true)}
          >
            {heroContent.videoSources.map((videoSource) => (
              <source
                key={videoSource.src}
                src={videoSource.src}
                type={videoSource.type}
              />
            ))}
          </video>
        )}
        <div className="hero__scrim" aria-hidden="true" />
      </div>

      <div className="hero__body">
        <div className="hero__content shell">
          <MaskedLines
            as="h1"
            lines={[
              heroContent.headlineLines[0],
              <em key="emphasis" className="hero__emphasis">
                {heroContent.headlineLines[1]}
              </em>,
            ]}
            className="hero__headline t-display"
            staggerMs={130}
            initialDelayMs={180}
          />

          <p className="hero__lead">{heroContent.lead}</p>
        </div>

        <div className="hero__base">
          <div className="hero__base-inner shell">
            <p className="hero__locale t-label">
              <span className="hero__locale-mark" aria-hidden="true" />
              {studioIdentity.location}
            </p>

            <button
              type="button"
              className="hero__scroll"
              onClick={handleScrollToNextSection}
            >
              <span className="hero__scroll-label t-label">
                {heroContent.scrollLabel}
              </span>
              <span className="hero__scroll-track" aria-hidden="true">
                <span className="hero__scroll-thumb" />
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Hero;
